import type {
  Ticket,
  Services,
  UserLimitedList,
  Project,
  Client,
  User,
  UserGroup,
} from "../generated-axios-client";
import { TECHNICIAN_GROUP_NAME } from "./dashboard.constants";

export function buildAssigneeById(assignees: UserLimitedList[]): Record<number, UserLimitedList> {
  return Object.fromEntries(assignees.map((a) => [a.id, a]));
}

export function buildServiceById(services: Services[]): Record<string, Services> {
  return Object.fromEntries(services.filter((s) => s.id).map((s) => [s.id!, s]));
}

export function buildClientById(clients: Client[]): Record<string, Client> {
  return Object.fromEntries(clients.filter((c) => c.id).map((c) => [c.id!, c]));
}

export function countByStatus(tickets: Ticket[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const t of tickets) {
    const s = t.status ?? "UNKNOWN";
    counts[s] = (counts[s] ?? 0) + 1;
  }
  return counts;
}

export function countByServiceId(tickets: Ticket[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const t of tickets) {
    if (t.service) {
      counts[t.service] = (counts[t.service] ?? 0) + 1;
    }
  }
  return counts;
}

export function findOverdueTickets(tickets: Ticket[]): Ticket[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return tickets.filter((t) => {
    if (!t.deadline) return false;
    if (t.status === "DONE") return false;
    return new Date(t.deadline) < today;
  });
}

export function findDueSoonTickets(tickets: Ticket[], days: number): Ticket[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const future = new Date(today);
  future.setDate(future.getDate() + days);
  return tickets.filter((t) => {
    if (!t.deadline) return false;
    if (t.status === "DONE") return false;
    const d = new Date(t.deadline);
    return d >= today && d < future;
  });
}

export function findProjectsEndingSoon(projects: Project[], days: number): Project[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const future = new Date(today);
  future.setDate(future.getDate() + days);
  return projects.filter((p) => {
    if (p.project_status !== "ACTIVE") return false;
    if (!p.end) return false;
    const d = new Date(p.end);
    return d >= today && d <= future;
  });
}

export function resolveTicketAssignees(
  ticket: Ticket,
  assigneeById: Record<number, UserLimitedList>,
): UserLimitedList[] {
  return ticket.assignees
    .map((a) => (a.assignee != null ? assigneeById[a.assignee] : undefined))
    .filter((u): u is UserLimitedList => u !== undefined);
}

export function getTechnicianGroupId(groups: UserGroup[]): number | null {
  return groups.find((g) => g.name === TECHNICIAN_GROUP_NAME)?.id ?? null;
}

export function filterActiveTechnicians(users: User[], techGroupId: number): User[] {
  return users.filter((u) => u.groups?.includes(techGroupId) && u.is_active !== false);
}

export function countWorkOrdersPerTechnician(
  tickets: Ticket[],
  technicians: User[],
): { user: User; open: number; total: number }[] {
  const openStatuses = new Set(["NEW", "IN_PROGRESS", "STANDBY", "IN_REVIEW"]);
  const openMap: Record<number, number> = {};
  const totalMap: Record<number, number> = {};

  for (const t of tickets) {
    for (const a of t.assignees) {
      if (a.assignee == null) continue;
      totalMap[a.assignee] = (totalMap[a.assignee] ?? 0) + 1;
      if (openStatuses.has(t.status ?? "")) {
        openMap[a.assignee] = (openMap[a.assignee] ?? 0) + 1;
      }
    }
  }

  return technicians
    .map((u) => ({
      user: u,
      open: openMap[u.id] ?? 0,
      total: totalMap[u.id] ?? 0,
    }))
    .sort((a, b) => b.open - a.open);
}
